import React from "react";
import { hoje, mesAtual } from "../../helpers/mesesHelper";
import { AppContext } from "../../contexts/AppContext";
import GerarCobrancasCards from "./GerarCobrancas/GerarCobrancasCards";
import useApi from "../../hooks/useApi";
import CobrancaApiClient from "../../api/cobranca";
import Card from "../../components/page/Card";
import GerarCobrancasDataTable from "./GerarCobrancas/GerarCobrancasDataTable";
import GerarCobrancasFiltros from "./GerarCobrancas/GerarCobrancasFiltros";
import CobrancaModal from "./GerarCobrancas/CobrancaModal";
import ClienteApiClient from "../../api/cliente";
import Validation from "../../helpers/Validation";
import ErrorSwal from "../../components/Swal/errorSwal";
import CommonSwal from "../../components/Swal/commonSwal";
import EmitirCobrancasModal from "./GerarCobrancas/EmitirCobrancasModal";
import AutorizarGeracaoModal from "./GerarCobrancas/AutorizarGeracaoModal";
import OptionsSwal from "../../components/Swal/optionsSwal";

function GerarCobrancas() {
  const [linhas, setLinhas] = React.useState([]);
  const { session } = React.useContext(AppContext);
  const [checkboxesMarcados, setCheckboxesMarcados] = React.useState([]);
  const [clientes, setClientes] = React.useState([]);
  const [cobrancaModal, setCobrancaModal] = React.useState(false);
  const [emitirCobrancaModal, setEmitirCobrancaModal] = React.useState(false);
  const [autorizarGeracaoModal, setAutorizarGeracaoModal] =
    React.useState(false);
  const [configuracoes, setConfiguracoes] = React.useState({});
  const [modalProps, setModalProps] = React.useState({});
  const [filtros, setFiltros] = React.useState({
    status: "TODAS",
    mes: mesAtual,
    ano: hoje.getFullYear(),
  });
  const [cards, setCards] = React.useState({});
  const [values, setValues] = React.useState({});

  const getCobrancasCb = React.useCallback(
    (mes = null, ano = null, status = null) => {
      return CobrancaApiClient.listCobrancas(
        session.empresa.replace(/E/gi, ""),
        {
          mes: mes ? mes : filtros.mes,
          ano: ano ? ano : filtros.ano,
          status: status
            ? status === "TODAS"
              ? null
              : status
            : filtros.status === "TODAS"
              ? null
              : filtros.status,
        }
      );
    },
    [filtros.ano, filtros.mes, filtros.status, session.empresa]
  );

  const { request: atualizarCobrancas, data: cobrancasResponse } =
    useApi(getCobrancasCb);
  const { request: getConfig, data: configResponse } = useApi(
    CobrancaApiClient.getConfig,
    false
  );
  const { request: getClientes, data: clientesResponse } = useApi(
    ClienteApiClient.listClientes
  );
  const getCobrancaApi = useApi(CobrancaApiClient.getCobranca);
  const putCobrancaApi = useApi(CobrancaApiClient.putCobranca);
  const postCobrancaApi = useApi(CobrancaApiClient.postCobranca);
  const emitirCobrancaApi = useApi(CobrancaApiClient.emitirCobranca);
  const autorizarGeracaoApi = useApi(CobrancaApiClient.autorizarGeracao);
  const deleteCobrancaApi = useApi(CobrancaApiClient.deleteCobranca);
  const copiarMesAnteriorApi = useApi(CobrancaApiClient.copiarMesAnterior);
  const reabrirEdicaoApi = useApi(CobrancaApiClient.reabrirEdicao);

  //Busca cobranças ao renderizar tela
  React.useEffect(() => {
    if (session.empresa) atualizarCobrancas();
  }, [atualizarCobrancas, session.empresa]);

  //Define o retorno da api nos valores das linhas da tabela e dos cards
  React.useEffect(() => {
    if (!cobrancasResponse) return;
    setLinhas(cobrancasResponse.cobrancas);
    setCards({ ...cobrancasResponse });
  }, [cobrancasResponse]);

  //Busca configurações ao renderizar tela
  React.useEffect(() => {
    if (session.empresa) getConfig(session.empresa.replace(/E/gi, ""));
  }, [getConfig, session.empresa]);

  //Define o retorno da api nos valores das configurações
  React.useEffect(() => {
    if (!configResponse) return;
    setConfiguracoes({
      descricao: configResponse.config.descricao_servico_prestado,
      tipo_vencimento: configResponse.config.tipo_vencimento,
      dia_vencimento: configResponse.config.dia,
      emitir_nfse: configResponse.config.emitir_nfse !== 0,
      enviar_email: configResponse.config.enviar_email !== 0,
      multa_juros: configResponse.config.multas_juros !== 0,
      tipo_multa: configResponse.config.tipo_multa,
      multa: configResponse.config.multa,
      tipo_juros: configResponse.config.tipo_juros,
      juros: configResponse.config.juros,
    });
  }, [configResponse]);

  //Busca clientes ao renderizar tela
  React.useEffect(() => {
    if (session.empresa) getClientes(session.empresa.replace(/E/gi, ""));
  }, [getClientes, session.empresa]);

  //Define o retorno da api nos valores de clientes
  React.useEffect(() => {
    if (!clientesResponse) return;
    setClientes(clientesResponse);
  }, [clientesResponse]);

  //Redefine valores de multa e juros
  React.useEffect(() => {
    if (!values.multa_juros)
      setValues((values) => ({
        ...values,
        tipo_juros: undefined,
        tipo_multa: undefined,
        multa: "0",
        juros: "0",
      }));
  }, [values.multa_juros]);

  const togNovaCobrancaModal = () => {
    setValues({
      valor_total: 0.0,
      multa_juros: true,
      emitir_nfse: true,
      emitir_boleto: true,
      enviar_email: true,
    });
    setConfig();
    setModalProps("nova");
    setCobrancaModal(true);
  };

  const togEditarCobrancaModal = async (uid) => {
    const result = await getCobrancaValues(uid);
    if (result) return;
    setModalProps("editar");
    setCobrancaModal(true);
  };

  const togVisualizarCobrancaModal = async (uid) => {
    const result = await getCobrancaValues(uid);
    if (result) return;
    setModalProps("visualizar");
    setCobrancaModal(true);
  };

  const togEmitirCobrancaModal = () => {
    setValues({
      opcao_emitir: true,
    });
    setEmitirCobrancaModal(true);
  };

  const togAutorizarGeracaoModal = () => {
    setValues({
      opcao_autorizacao: true,
    });
    setAutorizarGeracaoModal(true);
  };

  const setConfig = () => {
    if (Object.keys(configuracoes).length !== 0)
      setValues((values) => ({
        ...values,
        descricao: configuracoes.descricao,
        tipo_vencimento: configuracoes.tipo_vencimento,
        dia_vencimento: configuracoes.dia_vencimento,
        emitir_nfse: configuracoes.emitir_nfse,
        enviar_email: configuracoes.enviar_email,
        multa_juros: configuracoes.multa_juros,
        tipo_multa: configuracoes.tipo_multa,
        multa: configuracoes.multa,
        tipo_juros: configuracoes.tipo_juros,
        juros: configuracoes.juros,
      }));
  };

  const setCobrancaValues = (cobranca) => {
    setValues({
      ...values,
      cobranca_uid: cobranca.uid,
      cliente: cobranca.cliente_uid,
      descricao: cobranca.descricao,
      valor_total: cobranca.valor_total,
      tipo_vencimento: cobranca.tipo_vencimento,
      dia_vencimento: cobranca.dia_vencimento,
      multa_juros: cobranca.multa_juros,
      tipo_multa: cobranca.tipo_multa,
      multa: cobranca.multa,
      tipo_juros: cobranca.tipo_juros,
      juros: cobranca.juros,
      emitir_nfse: cobranca.emitir_nfse === 1,
      emitir_boleto: cobranca.emitir_boleto === 1,
      enviar_email: cobranca.enviar_email === 1,
      email: cobranca.emails,
    });
  };

  const getCobrancaValues = async (uid) => {
    const cobrancaResult = await getCobrancaApi.request(
      session.empresa.replace(/E/gi, ""),
      uid
    );
    if (!cobrancaResult) return true;
    setCobrancaValues(cobrancaResult);
    return false;
  };

  const salvaCobranca = async () => {
    const formData = new FormData();
    formData.append("mes", filtros.mes);
    formData.append("ano", filtros.ano);
    formData.append("cliente_uid", values.cliente);
    formData.append("descricao", values.descricao);
    formData.append("valor_total", values.valor_total);
    formData.append("tipo_vencimento", values.tipo_vencimento);
    formData.append("dia_vencimento", values.dia_vencimento);
    formData.append("multa_juros", values.multa_juros);
    formData.append("emitir_nfse", values.emitir_nfse ? 1 : 0);
    formData.append("emitir_boleto", values.emitir_boleto ? 1 : 0);
    formData.append("enviar_email", values.enviar_email ? 1 : 0);

    if (Validation.verificarForm(formData))
      return ErrorSwal({
        text: "Favor preencher todos os dados obrigatórios.",
      });

    formData.append("tipo_multa", values.multa_juros ? values.tipo_multa : 0);
    formData.append("multa", values.multa_juros ? values.multa : "");
    formData.append("tipo_juros", values.multa_juros ? values.tipo_juros : 0);
    formData.append("juros", values.multa_juros ? values.juros : "");

    if (
      values.multa_juros &&
      ["tipo_multa", "multa", "tipo_juros", "juros"].some(
        (field) => values[field] === ""
      )
    )
      return ErrorSwal({
        text: "Favor preencher todos os dados obrigatórios.",
      });

    formData.append("email", values.enviar_email ? values.email : "");

    if (values.enviar_email) {
      if (values.email === "" || !values.email)
        return ErrorSwal({
          text: "Favor preencher todos os dados obrigatórios.",
        });

      const emails = values.email;
      const erros = Validation.validarEmails(emails);
      if (erros)
        return ErrorSwal({
          text: "Por favor, forneça email(s) válido(s).",
        });
    }

    if (values.valor_total < 2.5)
      return ErrorSwal({
        text: "O valor mínimo da cobrança é R$ 2,50.",
      });

    var uid = values.cobranca_uid;

    const response = await putCobrancaApi.request(
      session.empresa.replace(/E/gi, ""),
      uid,
      formData
    );
    if (!response) return;
    CommonSwal({
      title: "Cobrança salva com sucesso!",
      icon: "success",
    }).then(async () => {
      setCobrancaModal(false);
      await atualizarCobrancas();
    });
  };

  const salvaNovaCobranca = async () => {
    const formData = new FormData();
    formData.append("mes", filtros.mes);
    formData.append("ano", filtros.ano);
    formData.append("cliente_uid", values.cliente);
    formData.append("descricao", values.descricao);
    formData.append("valor_total", values.valor_total);
    formData.append("tipo_vencimento", values.tipo_vencimento);
    formData.append("dia_vencimento", values.dia_vencimento);
    formData.append("multa_juros", values.multa_juros);
    formData.append("emitir_nfse", values.emitir_nfse ? 1 : 0);
    formData.append("emitir_boleto", values.emitir_boleto ? 1 : 0);
    formData.append("enviar_email", values.enviar_email ? 1 : 0);

    if (Validation.verificarForm(formData))
      return ErrorSwal({
        text: "Favor preencher todos os dados obrigatórios.",
      });

    formData.append("tipo_multa", values.multa_juros ? values.tipo_multa : 0);
    formData.append("multa", values.multa_juros ? values.multa : "");
    formData.append("tipo_juros", values.multa_juros ? values.tipo_juros : 0);
    formData.append("juros", values.multa_juros ? values.juros : "");

    if (
      values.multa_juros &&
      ["tipo_multa", "multa", "tipo_juros", "juros"].some(
        (field) => values[field] === ""
      )
    )
      return ErrorSwal({
        text: "Favor preencher todos os dados obrigatórios.",
      });

    formData.append("emails", values.enviar_email ? values.email : "");

    if (values.enviar_email) {
      if (values.email === "" || !values.email)
        return ErrorSwal({
          text: "Favor preencher todos os dados obrigatórios.",
        });

      const emails = values.email;
      const erros = Validation.validarEmails(emails);
      if (erros)
        return ErrorSwal({
          text: "Por favor, forneça email(s) válido(s).",
        });
    }

    if (values.valor_total < 2.5)
      return ErrorSwal({
        text: "O valor mínimo da cobrança é R$ 2,50.",
      });

    const response = await postCobrancaApi.request(
      session.empresa.replace(/E/gi, ""),
      formData
    );
    if (!response) return;
    CommonSwal({
      title: "Cobrança salva com sucesso!",
      icon: "success",
    }).then(async () => {
      setCobrancaModal(false);
      await atualizarCobrancas();
    });
  };

  const emitir = async () => {
    const formData = new FormData();

    if (values.opcao_emitir) {
      if (checkboxesMarcados.length === 0)
        return ErrorSwal({
          text: "Nenhuma cobrança selecionada.",
        });

      formData.append("ids", JSON.stringify(checkboxesMarcados));
    } else {
      const idsParaAppend = linhas.map((linha) => linha.uid);

      if (idsParaAppend.length === 0) {
        return ErrorSwal({
          text: "Não existem cobranças para realizar a emissão.",
        });
      }

      formData.append("ids", JSON.stringify(idsParaAppend));
    }

    const response = await emitirCobrancaApi.request(
      session.empresa.replace(/E/gi, ""),
      formData
    );
    if (!response) return;
    CommonSwal({
      title: "Cobranças emitidas!",
      icon: "success",
    }).then(async () => {
      setEmitirCobrancaModal(false);
      await atualizarCobrancas();
    });
  };

  const autorizarGeracao = async () => {
    const formData = new FormData();

    if (values.opcao_autorizacao) {
      if (checkboxesMarcados.length === 0)
        return ErrorSwal({
          text: "Nenhuma cobrança selecionada.",
        });

      formData.append("ids", JSON.stringify(checkboxesMarcados));
    } else {
      const idsParaAppend = linhas.map((linha) => linha.uid);

      if (idsParaAppend.length === 0) {
        return ErrorSwal({
          text: "Não existem cobranças para serem autorizadas.",
        });
      }

      formData.append("ids", JSON.stringify(idsParaAppend));
    }

    const response = await autorizarGeracaoApi.request(
      session.empresa.replace(/E/gi, ""),
      formData
    );
    if (!response) return;
    CommonSwal({
      title: "Geração autorizada!",
      icon: "success",
    }).then(async () => {
      setAutorizarGeracaoModal(false);
      await atualizarCobrancas();
    });
  };

  const apagarCobranca = (uid) => {
    OptionsSwal({
      title: "Você tem certeza que deseja excluir esta cobrança?",
      icon: "warning",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await deleteCobrancaApi.request(
          session.empresa.replace(/E/gi, ""),
          uid
        );
        if (!response) return;
        CommonSwal({
          title: "Cobrança excluída!",
          icon: "success",
        }).then(async () => {
          await atualizarCobrancas();
        });
      }
    });
  };

  const copiarMesAnterior = async () => {
    if (linhas.length > 0) {
      ErrorSwal({
        title:
          "A cópia dos dados de cobranças do mês anterior não pode ser realizada, pois já foram identificados registros no mês atual. Por favor, exclua os dados existentes para prosseguir com a cópia.",
      });
    } else {
      const formData = new FormData();
      formData.append("mes", filtros.mes);
      formData.append("ano", filtros.ano);

      const response = await copiarMesAnteriorApi.request(
        session.empresa.replace(/E/gi, ""),
        formData
      );
      if (!response) return;
      CommonSwal({
        title: "Cobranças copiadas com sucesso!",
        icon: "success",
      }).then(async () => {
        setEmitirCobrancaModal(false);
        await atualizarCobrancas();
      });
    }
  };

  const reabrirEdicao = (uid) => {
    OptionsSwal({
      title: "Você deseja reabrir a edição dessa cobrança?",
      icon: "warning",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await reabrirEdicaoApi.request(
          session.empresa.replace(/E/gi, ""),
          uid
        );
        if (!response) return;
        CommonSwal({
          title: "Edição aberta!",
          icon: "success",
        }).then(async () => {
          await atualizarCobrancas();
        });
      }
    });
  };

  return (
    <>
      <div className="row">
        <GerarCobrancasCards cards={cards} />
      </div>
      <Card>
        <div className="row">
          <GerarCobrancasFiltros filtrosHook={{ filtros, setFiltros }} />
          <br />
          <GerarCobrancasDataTable
            linhas={linhas}
            filtros={filtros}
            checkboxesHook={{ checkboxesMarcados, setCheckboxesMarcados }}
            togNovaCobrancaModal={togNovaCobrancaModal}
            togEditarCobrancaModal={togEditarCobrancaModal}
            togVisualizarCobrancaModal={togVisualizarCobrancaModal}
            togEmitirCobrancaModal={togEmitirCobrancaModal}
            togAutorizarGeracaoModal={togAutorizarGeracaoModal}
            apagarCobranca={apagarCobranca}
            copiarMesAnterior={copiarMesAnterior}
            reabrirEdicao={reabrirEdicao}
          />
        </div>
      </Card>
      <CobrancaModal
        modalProps={modalProps}
        clientes={clientes}
        cobrancaModalHook={{ cobrancaModal, setCobrancaModal }}
        valuesHook={{ values, setValues }}
        salvaCobranca={salvaCobranca}
        salvaNovaCobranca={salvaNovaCobranca}
      />
      <EmitirCobrancasModal
        emitirModalHook={{ emitirCobrancaModal, setEmitirCobrancaModal }}
        valuesHook={{ values, setValues }}
        emitir={emitir}
      />
      <AutorizarGeracaoModal
        autorizarModalHook={{ autorizarGeracaoModal, setAutorizarGeracaoModal }}
        valuesHook={{ values, setValues }}
        autorizarGeracao={autorizarGeracao}
      />
    </>
  );
}

export default GerarCobrancas;
